import { createSlice } from "@reduxjs/toolkit";
import { cartItemKey, unitPrice, validDiscount, productStock } from "../../../utils/cartPricing";

const stockLimit = (stock) => stock == null ? Infinity : Math.max(0, Math.floor(Number(stock) || 0));

function normalizeItem(item) {
  if (!item || typeof item.productId !== "string" || !item.productId) return null;
  const quantity = Number(item.quantity);
  const basePrice = Number(item.basePrice ?? item.originalPrice ?? item.price);
  if (!Number.isInteger(quantity) || quantity <= 0 || !Number.isFinite(basePrice) || basePrice < 0) return null;
  const { token, isLoggedIn, ...safeItem } = item;
  const normalized = {
    ...safeItem,
    basePrice,
    quantity: Math.min(quantity, stockLimit(item.stock)),
    quantityDiscount: validDiscount(item.quantityDiscount) ? item.quantityDiscount : null,
  };
  if (normalized.quantity <= 0) return null;
  normalized.discountedPrice = unitPrice(normalized);
  normalized.appliedDiscount = normalized.quantityDiscount && normalized.quantity >= Number(normalized.quantityDiscount.quantity)
    ? normalized.quantityDiscount : null;
  return normalized;
}

export function loadSavedCart() {
  try {
    const saved = JSON.parse(sessionStorage.getItem("cart") || "[]");
    return normalizeSavedCart(saved);
  } catch {
    return [];
  }
}

export const normalizeSavedCart = (items) => Array.isArray(items) ? items.map(item => {
  const normalized = normalizeItem({ ...item, stock: null });
  return normalized ? { ...normalized, stock: item.stock, name: "Produkt", catalogVerified: false,
    unavailable: Boolean(item.unavailable) || normalized.quantity > stockLimit(item.stock) } : null;
}).filter(Boolean) : [];

function persistCart(items) {
  try { sessionStorage.setItem("cart", JSON.stringify(items)); } catch {
    // Storage can be disabled or full. The current cart remains usable.
  }
}

const cartSlice = createSlice({
  name: "cart",
  initialState: () => ({ items: loadSavedCart() }),
  reducers: {
    beginCatalogRefresh: state => { state.catalogStatus = 'loading'; },
    refreshCartProducts: (state, { payload }) => {
      state.catalogStatus = payload.some(result => result.error) ? 'error' : 'ready';
      state.items = state.items.map(item => {
        const result = payload.find(entry => entry.id === item.productId);
        if (!result) return item;
        if (!result.product) return { ...item, name: 'Produkt', unavailable: Boolean(result.unavailable), catalogVerified: false };
        const product = result.product;
        const stock = productStock(product);
        const basePrice = Number(product.finalPrice ?? product.price);
        if (!Number.isFinite(basePrice) || basePrice < 0) return { ...item, name: 'Produkt', unavailable: true, catalogVerified: false };
        const updated = { ...item, name: product.name || 'Produkt', sku: product.sku || item.sku,
          imageUrl: product.images?.[0] || '', deliveryNote: product.deliveryNote || '', color: product.color || item.color,
          basePrice, price: basePrice, originalPrice: basePrice, stock,
          quantityDiscount: validDiscount(product.quantityDiscount) ? product.quantityDiscount : null,
          unavailable: stock < item.quantity, catalogVerified: true };
        updated.discountedPrice = unitPrice(updated);
        return updated;
      });
      persistCart(state.items);
    },
    addItemToCart: (state, { payload }) => {
      const item = normalizeItem(payload);
      if (!item) return;
      const index = state.items.findIndex((existing) => cartItemKey(existing) === cartItemKey(item));
      if (index < 0) {
        state.items.push(item);
      } else {
        const current = state.items[index];
        state.items[index] = normalizeItem({
          ...current, ...item,
          quantity: Math.min(Number(current.quantity) + item.quantity, stockLimit(item.stock ?? current.stock)),
        });
      }
      state.items = state.items.filter(Boolean);
      persistCart(state.items);
    },
    removeItemFromCart: (state, { payload }) => {
      state.items = state.items.filter((item) => cartItemKey(item) !== cartItemKey(payload));
      persistCart(state.items);
    },
    updateItemQuantity: (state, { payload }) => {
      const quantity = Number(payload.quantity);
      if (!Number.isInteger(quantity) || quantity < 0) return;
      const index = state.items.findIndex((item) => cartItemKey(item) === cartItemKey(payload));
      if (index < 0) return;
      const current = state.items[index];
      if (quantity === 0) {
        state.items.splice(index, 1);
      } else {
        const updated = normalizeItem({ ...current, quantity, stock: payload.stock ?? current.stock });
        if (updated) state.items[index] = { ...updated, unavailable: updated.stock != null && updated.quantity > updated.stock };
      }
      persistCart(state.items);
    },
    clearCart: (state) => {
      state.items = [];
      persistCart(state.items);
    },
  },
});

export const { addItemToCart, removeItemFromCart, updateItemQuantity, clearCart, beginCatalogRefresh, refreshCartProducts } = cartSlice.actions;
export default cartSlice.reducer;
